import React, { useEffect, useMemo, useRef, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "@/components/ui/use-toast";
import { supabase } from "@/lib/supabase";
import {
  loadPayslipConfig,
  savePayslipConfig,
  DEFAULT_PAYSLIP_CONFIG,
  AVAILABLE_SOURCE_COLUMNS,
  PAYSLIP_PLACEHOLDERS,
  type PayslipConfig,
  type PayslipColumn,
  type PayslipElement,
} from "@/lib/payslipConfig";
import {
  buildRateContext,
  flatCommissionPercent,
  type ResolvedRateContext,
  type PaymentTypeRate,
} from "@/lib/paymentRates";
import PayslipPreview from "./PayslipPreview";
import {
  ArrowUp,
  ArrowDown,
  Trash2,
  Save,
  RefreshCw,
  RotateCcw,
  LayoutTemplate,
  Eye,
  EyeOff,
  Image as ImageIcon,
  Heading as HeadingIcon,
  Type,
  Tag,
  StretchHorizontal,
  AlignLeft,
  AlignCenter,
  AlignRight,
  Columns3,
  Upload,
  GripVertical,
  PanelLeft,
  Table2,
  Receipt,
} from "lucide-react";

const DEFAULT_TENANT_ID = "1225f4cb-43ae-463d-a6b0-53f721362dbf";

const norm = (s: string | null | undefined) => (s || "").toString().trim().toLowerCase();

const uid = () => `el-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`;

/** A config saved before the blocks feature existed has no `elements` at
 * all -- the real payslip falls back to its old fixed layout using the
 * branding fields directly (see payslip.ts), so nothing breaks. The FIRST
 * time the builder loads one of these, we synthesize a starter block
 * sequence FROM that existing branding (not the hardcoded factory default)
 * so nothing the admin already set (logo, tagline, footer note) gets lost
 * the moment they touch the builder and save. */
const synthesizeElementsFromBranding = (cfg: PayslipConfig): PayslipElement[] => [
  { id: uid(), type: "logo", value: cfg.branding.logoUrl || "", align: "center" },
  { id: uid(), type: "heading", value: cfg.branding.companyName || "Weekly Payment Summary", align: "center" },
  { id: uid(), type: "tagline", value: cfg.branding.tagline || "", align: "center" },
  { id: uid(), type: "daily_table", value: "", align: "center" },
  { id: uid(), type: "summary", value: "", align: "center" },
  { id: uid(), type: "footer", value: cfg.branding.footerNote || "", align: "center" },
];

const resolveCommission = (
  ctx: ResolvedRateContext | null,
  driverName: string,
  paymentTypeName: string | null,
  flatPercent: number,
): { pct: number; source: "override" | "standard" | "flat"; exists: boolean } => {
  if (!paymentTypeName) return { pct: flatPercent, source: "flat", exists: true };
  if (!ctx) return { pct: flatPercent, source: "flat", exists: false };
  const pt: PaymentTypeRate | undefined = ctx.byName[norm(paymentTypeName)];
  const exists = !!pt;
  if (pt && driverName) {
    const ov = ctx.overrides[driverName];
    if (ov && ov[pt.id] !== undefined) {
      return { pct: Number(ov[pt.id]), source: "override", exists };
    }
  }
  if (pt && pt.base_commission_percent !== null && pt.base_commission_percent !== undefined) {
    return { pct: Number(pt.base_commission_percent), source: "standard", exists };
  }
  return { pct: flatPercent, source: "flat", exists };
};

/* Palette item definitions for the "content" blocks (left panel). */
const ELEMENT_PALETTE: {
  type: PayslipElement["type"];
  label: string;
  hint: string;
  icon: typeof Type;
  defaultValue: string;
}[] = [
  { type: "logo", label: "Logo", hint: "Add a company logo image", icon: ImageIcon, defaultValue: "" },
  { type: "heading", label: "Heading", hint: "Large bold title line", icon: HeadingIcon, defaultValue: "Weekly Payment Summary" },
  { type: "text", label: "Text", hint: "A line of text", icon: Type, defaultValue: "Add your text here" },
  { type: "tagline", label: "Tagline", hint: "Small sub-heading", icon: Tag, defaultValue: "Your tagline" },
  { type: "footer", label: "Footer Note", hint: "Small note at the bottom", icon: AlignLeft, defaultValue: "Paid every Wednesday" },
  { type: "spacer", label: "Spacer", hint: "Vertical gap", icon: StretchHorizontal, defaultValue: "" },
  { type: "daily_table", label: "Amounts Table", hint: "Date, jobs & your turnover columns", icon: Table2, defaultValue: "" },
  { type: "summary", label: "Payment Summary", hint: "Gross Turnover through to Total Payment", icon: Receipt, defaultValue: "" },
];

/** Block types that only make sense once on a payslip -- same "used" pattern as a Turnover Column. */
const SINGLE_INSTANCE_TYPES: PayslipElement["type"][] = ["daily_table", "summary"];

const DriverPaymentBuilder: React.FC = () => {
  const [config, setConfig] = useState<PayslipConfig>({
    ...DEFAULT_PAYSLIP_CONFIG,
    elements: [],
  });
  const [rateCtx, setRateCtx] = useState<ResolvedRateContext | null>(null);
  const [flatPercent, setFlatPercent] = useState<number>(flatCommissionPercent(null));
  const [sampleDriver, setSampleDriver] = useState<string>("");
  const [driverNames, setDriverNames] = useState<string[]>([]);
  const [sampleExGst] = useState<number>(100);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploadingId, setUploadingId] = useState<string | null>(null);
  const [dropActive, setDropActive] = useState<"elements" | "columns" | null>(null);
  const fileInputs = useRef<Record<string, HTMLInputElement | null>>({});

  const load = async () => {
    setLoading(true);
    try {
      const { data: drv } = await supabase
        .from("drivers")
        .select("driver_name")
        .order("driver_name");
      const names = Array.from(
        new Set((drv || []).map((d: any) => d.driver_name).filter(Boolean)),
      ) as string[];
      setDriverNames(names);

      const { data: comm } = await supabase
        .from("settings")
        .select("value")
        .eq("name", "commission_percent")
        .maybeSingle();
      const flat = comm?.value != null && comm.value !== ""
        ? Number(comm.value)
        : flatCommissionPercent(null);
      setFlatPercent(Number.isFinite(flat) ? flat : flatCommissionPercent(null));

      const [cfg, ctx] = await Promise.all([
        loadPayslipConfig(DEFAULT_TENANT_ID),
        buildRateContext(names, DEFAULT_TENANT_ID),
      ]);
      setConfig({ ...cfg, elements: cfg.elements ?? synthesizeElementsFromBranding(cfg) });
      setRateCtx(ctx);
    } catch (e: any) {
      toast({
        title: "Could not load builder",
        description: e?.message ?? String(e),
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const paymentTypeNames = useMemo(() => {
    const names = new Set<string>();
    (rateCtx?.paymentTypes || []).forEach((p) => p.name && names.add(p.name));
    config.columns.forEach((c) => c.paymentType && names.add(c.paymentType));
    return Array.from(names).sort((a, b) => a.localeCompare(b));
  }, [rateCtx, config.columns]);

  const usedColumnKeys = useMemo(
    () => new Set(config.columns.map((c) => c.key)),
    [config.columns],
  );

  /* ---------------- Element helpers ---------------- */
  const elements = config.elements ?? [];

  const addElement = (type: PayslipElement["type"]) => {
    const def = ELEMENT_PALETTE.find((p) => p.type === type);
    const el: PayslipElement = {
      id: uid(),
      type,
      value: def?.defaultValue ?? "",
      align: "center",
    };
    setConfig((prev) => ({ ...prev, elements: [...(prev.elements ?? []), el] }));
  };

  const updateElement = (id: string, patch: Partial<PayslipElement>) => {
    setConfig((prev) => ({
      ...prev,
      elements: (prev.elements ?? []).map((e) => (e.id === id ? { ...e, ...patch } : e)),
    }));
  };

  const removeElement = (id: string) => {
    setConfig((prev) => ({
      ...prev,
      elements: (prev.elements ?? []).filter((e) => e.id !== id),
    }));
  };

  const moveElement = (id: string, dir: -1 | 1) => {
    setConfig((prev) => {
      const list = [...(prev.elements ?? [])];
      const idx = list.findIndex((e) => e.id === id);
      const j = idx + dir;
      if (idx < 0 || j < 0 || j >= list.length) return prev;
      [list[idx], list[j]] = [list[j], list[idx]];
      return { ...prev, elements: list };
    });
  };

  const uploadLogo = async (file: File, id: string) => {
    setUploadingId(id);
    try {
      const ext = file.name.split(".").pop();
      const path = `payslip-logos/logo-${Date.now()}.${ext}`;
      const { error } = await supabase.storage
        .from("app-branding")
        .upload(path, file, { cacheControl: "3600", upsert: true });
      if (error) throw error;
      const { data } = supabase.storage.from("app-branding").getPublicUrl(path);
      updateElement(id, { value: data.publicUrl });
      toast({ title: "Logo uploaded", description: "Remember to Save the layout." });
    } catch (e: any) {
      toast({
        title: "Upload failed",
        description: e?.message ?? String(e),
        variant: "destructive",
      });
    } finally {
      setUploadingId(null);
    }
  };

  /* ---------------- Column helpers ---------------- */
  const updateColumn = (idx: number, patch: Partial<PayslipColumn>) => {
    setConfig((prev) => ({
      ...prev,
      columns: prev.columns.map((c, i) => (i === idx ? { ...c, ...patch } : c)),
    }));
  };

  const moveColumn = (idx: number, dir: -1 | 1) => {
    setConfig((prev) => {
      const cols = [...prev.columns];
      const j = idx + dir;
      if (j < 0 || j >= cols.length) return prev;
      [cols[idx], cols[j]] = [cols[j], cols[idx]];
      return { ...prev, columns: cols };
    });
  };

  const removeColumn = (idx: number) => {
    setConfig((prev) => ({
      ...prev,
      columns: prev.columns.filter((_, i) => i !== idx),
    }));
  };

  const addColumn = (key: string) => {
    const source = AVAILABLE_SOURCE_COLUMNS.find((s) => s.key === key);
    if (!source) return;
    setConfig((prev) => ({
      ...prev,
      columns: [
        ...prev.columns,
        { key: source.key, label: source.label, paymentType: null, visible: true },
      ],
    }));
  };

  /* ---------------- Drag & drop ---------------- */
  const onDragStart = (e: React.DragEvent, payload: string) => {
    e.dataTransfer.setData("text/plain", payload);
    e.dataTransfer.effectAllowed = "copy";
  };

  const handleDrop = (e: React.DragEvent, zone: "elements" | "columns") => {
    e.preventDefault();
    setDropActive(null);
    const raw = e.dataTransfer.getData("text/plain");
    if (!raw) return;
    try {
      const data = JSON.parse(raw);
      if (data.kind === "element" && zone === "elements") {
        addElement(data.type);
      } else if (data.kind === "column" && zone === "columns") {
        addColumn(data.key);
      }
    } catch {
      /* ignore */
    }
  };

  const resetToDefault = () => {
    if (!window.confirm("Reset the layout and branding back to the original default?")) return;
    setConfig({
      columns: DEFAULT_PAYSLIP_CONFIG.columns.map((c) => ({ ...c })),
      branding: { ...DEFAULT_PAYSLIP_CONFIG.branding },
      elements: (DEFAULT_PAYSLIP_CONFIG.elements || []).map((e) => ({ ...e, id: uid() })),
    });
    toast({ title: "Reset", description: "Loaded the original default layout. Remember to Save." });
  };

  const save = async () => {
    if (!config.columns.length) {
      toast({
        title: "Add at least one column",
        description: "The summary needs at least one turnover column.",
        variant: "destructive",
      });
      return;
    }
    setSaving(true);
    try {
      await savePayslipConfig(config, DEFAULT_TENANT_ID);
      toast({
        title: "Payment builder saved",
        description: "The driver payment summary will now use this layout.",
      });
    } catch (e: any) {
      toast({
        title: "Not saved",
        description: e?.message ?? String(e),
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  const updateBranding = (patch: Partial<PayslipConfig["branding"]>) => {
    setConfig((prev) => ({ ...prev, branding: { ...prev.branding, ...patch } }));
  };

  const alignButton = (
    current: PayslipElement["align"],
    value: PayslipElement["align"],
    onClick: () => void,
    Icon: typeof AlignLeft,
  ) => (
    <button
      type="button"
      onClick={onClick}
      className={`flex h-7 w-7 items-center justify-center rounded border ${
        current === value
          ? "border-yellow-500 bg-yellow-500/20 text-yellow-300"
          : "border-border text-muted-foreground hover:text-foreground"
      }`}
    >
      <Icon className="h-3.5 w-3.5" />
    </button>
  );

  return (
    <Card className="shadow-lg bg-card border border-border">
      <CardHeader className="border-b border-border">
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div>
            <CardTitle className="text-xl text-primary flex items-center gap-2">
              <LayoutTemplate className="w-5 h-5" />
              Driver Payment Builder
            </CardTitle>
            <p className="text-sm text-muted-foreground mt-1">
              Build the payment summary like a web form. Drag items from the left panel onto
              the layout — a logo, headings, text, the amounts table, turnover columns — then
              use the arrows to reorder them and watch the mock PDF on the right update live.
              This is the exact layout the real Weekly Summary PDF and email use.
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={load} disabled={loading}>
              <RefreshCw className="w-4 h-4 mr-2" />
              {loading ? "Refreshing..." : "Refresh"}
            </Button>
            <Button variant="outline" onClick={resetToDefault}>
              <RotateCcw className="w-4 h-4 mr-2" />
              Reset
            </Button>
            <Button onClick={save} disabled={saving} className="golden-button">
              <Save className="w-4 h-4 mr-2" />
              {saving ? "Saving..." : "Save Layout"}
            </Button>
          </div>
        </div>
      </CardHeader>

      <CardContent className="p-4 md:p-5">
        <div className="grid grid-cols-1 xl:grid-cols-[230px_1fr_minmax(320px,420px)] gap-5">
          {/* ---------------- Left palette ---------------- */}
          <aside className="space-y-5">
            <div className="rounded-xl border border-yellow-900/30 bg-black/20 p-3">
              <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-foreground">
                <PanelLeft className="h-4 w-4 text-yellow-500" />
                Add to layout
              </div>
              <p className="text-[11px] text-muted-foreground mb-3">
                Drag onto the layout, or click to add.
              </p>

              <div className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-yellow-500/80">
                Branding &amp; Content
              </div>
              <div className="space-y-2">
                {ELEMENT_PALETTE.map((item) => {
                  const Icon = item.icon;
                  // Some blocks only make sense once on a payslip -- once
                  // placed, disable them here like a used column.
                  const alreadyPlaced =
                    SINGLE_INSTANCE_TYPES.includes(item.type) &&
                    elements.some((e) => e.type === item.type);
                  return (
                    <div
                      key={item.type}
                      draggable={!alreadyPlaced}
                      onDragStart={(e) =>
                        !alreadyPlaced &&
                        onDragStart(e, JSON.stringify({ kind: "element", type: item.type }))
                      }
                      onClick={() => !alreadyPlaced && addElement(item.type)}
                      className={`group flex items-center gap-2 rounded-lg border px-2.5 py-2 ${
                        alreadyPlaced
                          ? "cursor-not-allowed border-border bg-background/30 opacity-50"
                          : "cursor-grab border-border bg-background/60 hover:border-yellow-500/50 active:cursor-grabbing"
                      }`}
                    >
                      <GripVertical className="h-3.5 w-3.5 text-muted-foreground" />
                      <span className="flex h-7 w-7 items-center justify-center rounded-md bg-yellow-500/15 text-yellow-400">
                        <Icon className="h-4 w-4" />
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block text-sm font-medium leading-tight">{item.label}</span>
                        <span className="block text-[10px] text-muted-foreground leading-tight">
                          {item.hint}
                        </span>
                      </span>
                      {alreadyPlaced && (
                        <span className="text-[10px] text-muted-foreground">on layout</span>
                      )}
                    </div>
                  );
                })}
              </div>

              <div className="mb-2 mt-4 text-[11px] font-semibold uppercase tracking-wide text-yellow-500/80">
                Turnover Columns
              </div>
              <div className="space-y-2">
                {AVAILABLE_SOURCE_COLUMNS.map((col) => {
                  const used = usedColumnKeys.has(col.key);
                  return (
                    <div
                      key={col.key}
                      draggable={!used}
                      onDragStart={(e) =>
                        onDragStart(e, JSON.stringify({ kind: "column", key: col.key }))
                      }
                      onClick={() => !used && addColumn(col.key)}
                      className={`flex items-center gap-2 rounded-lg border px-2.5 py-2 ${
                        used
                          ? "cursor-not-allowed border-border bg-background/30 opacity-50"
                          : "cursor-grab border-border bg-background/60 hover:border-yellow-500/50 active:cursor-grabbing"
                      }`}
                    >
                      <Columns3 className="h-3.5 w-3.5 text-muted-foreground" />
                      <span className="flex-1 text-sm font-medium">{col.label}</span>
                      {used ? (
                        <span className="text-[10px] text-muted-foreground">on layout</span>
                      ) : (
                        <span className="text-[10px] text-yellow-500">add</span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </aside>

          {/* ---------------- Middle: layout canvas ---------------- */}
          <div className="space-y-5">
            {/* Content blocks drop zone */}
            <div
              onDragOver={(e) => {
                e.preventDefault();
                setDropActive("elements");
              }}
              onDragLeave={() => setDropActive((z) => (z === "elements" ? null : z))}
              onDrop={(e) => handleDrop(e, "elements")}
              className={`rounded-xl border-2 border-dashed p-3 transition-colors ${
                dropActive === "elements"
                  ? "border-yellow-500 bg-yellow-500/5"
                  : "border-yellow-900/30 bg-black/20"
              }`}
            >
              <div className="mb-2 flex items-center justify-between">
                <h3 className="font-semibold text-foreground">Header / Content Blocks</h3>
                <span className="text-[11px] text-muted-foreground">
                  {elements.length} block{elements.length === 1 ? "" : "s"}
                </span>
              </div>

              {elements.length === 0 ? (
                <div className="rounded-lg border border-dashed border-border py-6 text-center text-sm text-muted-foreground">
                  Drag a Logo, Heading or Text block here to start building your payment summary header.
                </div>
              ) : (
                <div className="space-y-2">
                  {elements.map((el, idx) => {
                    const paletteItem = ELEMENT_PALETTE.find((p) => p.type === el.type);
                    const Icon = paletteItem?.icon ?? Type;
                    return (
                      <div
                        key={el.id}
                        className="rounded-lg border border-border bg-background/50 p-3"
                      >
                        <div className="flex items-center gap-2">
                          <span className="flex h-7 w-7 items-center justify-center rounded-md bg-yellow-500/15 text-yellow-400">
                            <Icon className="h-4 w-4" />
                          </span>
                          <span className="text-sm font-semibold">{paletteItem?.label ?? el.type}</span>
                          <div className="ml-auto flex items-center gap-1">
                            {el.type !== "spacer" && el.type !== "daily_table" && el.type !== "summary" && (
                              <>
                                {alignButton(el.align, "left", () => updateElement(el.id, { align: "left" }), AlignLeft)}
                                {alignButton(el.align, "center", () => updateElement(el.id, { align: "center" }), AlignCenter)}
                                {alignButton(el.align, "right", () => updateElement(el.id, { align: "right" }), AlignRight)}
                              </>
                            )}
                            <Button
                              size="icon"
                              variant="ghost"
                              className="h-7 w-7"
                              disabled={idx === 0}
                              onClick={() => moveElement(el.id, -1)}
                              title="Move up"
                            >
                              <ArrowUp className="h-4 w-4" />
                            </Button>
                            <Button
                              size="icon"
                              variant="ghost"
                              className="h-7 w-7"
                              disabled={idx === elements.length - 1}
                              onClick={() => moveElement(el.id, 1)}
                              title="Move down"
                            >
                              <ArrowDown className="h-4 w-4" />
                            </Button>
                            <Button
                              size="icon"
                              variant="ghost"
                              className="h-7 w-7 text-red-400"
                              onClick={() => removeElement(el.id)}
                              title="Remove"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>

                        {el.type === "logo" ? (
                          <div className="mt-2 space-y-2">
                            {el.value && (
                              <div className="rounded-md border border-border bg-white/5 p-2">
                                <img
                                  src={el.value}
                                  alt="Logo"
                                  className="h-12 max-w-[180px] object-contain"
                                />
                              </div>
                            )}
                            <div className="flex flex-col gap-2 sm:flex-row">
                              <Input
                                value={el.value}
                                onChange={(e) => updateElement(el.id, { value: e.target.value })}
                                placeholder="https://…/logo.png"
                                className="dark-input h-9"
                              />
                              <input
                                ref={(r) => {
                                  fileInputs.current[el.id] = r;
                                }}

                                type="file"
                                accept="image/png,image/jpeg,image/svg+xml,image/webp"
                                className="hidden"
                                onChange={(e) => {
                                  const f = e.target.files?.[0];
                                  if (f) uploadLogo(f, el.id);
                                }}
                              />
                              <Button
                                type="button"
                                variant="outline"
                                className="h-9 whitespace-nowrap"
                                disabled={uploadingId === el.id}
                                onClick={() => fileInputs.current[el.id]?.click()}
                              >
                                <Upload className="h-4 w-4 mr-1" />
                                {uploadingId === el.id ? "Uploading…" : "Upload"}
                              </Button>
                            </div>
                            <p className="text-[11px] text-muted-foreground">
                              Paste an image URL or upload one. This is the logo that prints on the payment summary.
                            </p>
                          </div>
                        ) : el.type === "spacer" ? (
                          <p className="mt-2 text-[11px] text-muted-foreground">
                            Adds a blank vertical gap.
                          </p>
                        ) : el.type === "daily_table" ? (
                          <div className="mt-3 space-y-2">
                            <p className="text-[11px] text-muted-foreground">
                              Prints the date/jobs table with these turnover columns. Heads up: a
                              column's tick here also decides what counts toward the driver's Gross
                              Turnover, Commission and Total Payment on the real summary — turning one
                              off removes its dollars from their pay, not just from the printed table.
                            </p>
                            <div
                              onDragOver={(e) => {
                                e.preventDefault();
                                setDropActive("columns");
                              }}
                              onDragLeave={() => setDropActive((z) => (z === "columns" ? null : z))}
                              onDrop={(e) => handleDrop(e, "columns")}
                              className={`rounded-lg border-2 border-dashed p-2 transition-colors ${
                                dropActive === "columns"
                                  ? "border-yellow-500 bg-yellow-500/5"
                                  : "border-yellow-900/20 bg-black/10"
                              }`}
                            >
                              {config.columns.length === 0 ? (
                                <div className="rounded-lg border border-dashed border-border py-4 text-center text-xs text-muted-foreground">
                                  Drag a Turnover Column here (e.g. Cash, EFTPOS, eCard) from the left panel.
                                </div>
                              ) : (
                                <div className="space-y-2">
                                  {config.columns.map((col, idx) => {
                                    const { pct } = resolveCommission(
                                      rateCtx,
                                      sampleDriver,
                                      col.paymentType,
                                      flatPercent,
                                    );
                                    const driverKeeps = (sampleExGst * pct) / 100;
                                    return (
                                      <div
                                        key={`${col.key}-${idx}`}
                                        className={`rounded-lg border border-border bg-background/50 p-3 ${
                                          col.visible ? "" : "opacity-60"
                                        }`}
                                      >
                                        <div className="grid grid-cols-1 md:grid-cols-12 gap-2 items-end">
                                          <div className="md:col-span-4">
                                            <Label className="text-xs text-yellow-500">Label</Label>
                                            <Input
                                              value={col.label}
                                              onChange={(e) => updateColumn(idx, { label: e.target.value })}
                                              className="dark-input h-9"
                                            />
                                          </div>
                                          <div className="md:col-span-3">
                                            <Label className="text-xs text-yellow-500">Source</Label>
                                            <select
                                              value={col.key}
                                              onChange={(e) => updateColumn(idx, { key: e.target.value })}
                                              className="w-full h-9 rounded-md border border-input bg-background px-2 text-sm text-foreground"
                                            >
                                              {AVAILABLE_SOURCE_COLUMNS.map((s) => (
                                                <option key={s.key} value={s.key}>
                                                  {s.label}
                                                </option>
                                              ))}
                                            </select>
                                          </div>
                                          <div className="md:col-span-3">
                                            <Label className="text-xs text-yellow-500">Commission %</Label>
                                            <select
                                              value={col.paymentType ?? ""}
                                              onChange={(e) =>
                                                updateColumn(idx, {
                                                  paymentType: e.target.value === "" ? null : e.target.value,
                                                })
                                              }
                                              className="w-full h-9 rounded-md border border-input bg-background px-2 text-sm text-foreground"
                                            >
                                              <option value="">Flat company %</option>
                                              {paymentTypeNames.map((n) => (
                                                <option key={n} value={n}>
                                                  {n}
                                                </option>
                                              ))}
                                            </select>
                                          </div>
                                          <div className="md:col-span-2 flex items-center gap-1 justify-end">
                                            <Button
                                              size="icon"
                                              variant="ghost"
                                              className="h-8 w-8"
                                              title={col.visible ? "Hide (also stops counting toward pay)" : "Show (also counts toward pay)"}
                                              onClick={() => updateColumn(idx, { visible: !col.visible })}
                                            >
                                              {col.visible ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                                            </Button>
                                            <Button
                                              size="icon"
                                              variant="ghost"
                                              className="h-8 w-8"
                                              disabled={idx === 0}
                                              onClick={() => moveColumn(idx, -1)}
                                              title="Move up"
                                            >
                                              <ArrowUp className="h-4 w-4" />
                                            </Button>
                                            <Button
                                              size="icon"
                                              variant="ghost"
                                              className="h-8 w-8"
                                              disabled={idx === config.columns.length - 1}
                                              onClick={() => moveColumn(idx, 1)}
                                              title="Move down"
                                            >
                                              <ArrowDown className="h-4 w-4" />
                                            </Button>
                                            <Button
                                              size="icon"
                                              variant="ghost"
                                              className="h-8 w-8 text-red-400"
                                              onClick={() => removeColumn(idx)}
                                              title="Remove"
                                            >
                                              <Trash2 className="h-4 w-4" />
                                            </Button>
                                          </div>
                                        </div>
                                        <div className="mt-2 rounded-md bg-black/30 px-3 py-1.5 text-[11px] text-muted-foreground">
                                          Driver gets{" "}
                                          <span className="font-semibold text-green-400">{pct.toFixed(1)}%</span>{" "}
                                          of ${sampleExGst.toFixed(2)} ex-GST ={" "}
                                          <span className="font-semibold text-green-400">
                                            ${driverKeeps.toFixed(2)}
                                          </span>
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          </div>
                        ) : el.type === "summary" ? (
                          <p className="mt-2 text-[11px] text-muted-foreground">
                            Prints the Gross Turnover → Commission → Bonus → Deductions → Total
                            Payment card.
                          </p>
                        ) : (
                          <div className="mt-2 space-y-1">
                            <div className="flex flex-col gap-2 sm:flex-row">
                              <Input
                                value={el.value}
                                onChange={(e) => updateElement(el.id, { value: e.target.value })}
                                placeholder={`Enter ${el.type} text`}
                                className="dark-input h-9 flex-1"
                              />
                              <select
                                value=""
                                onChange={(e) => {
                                  const token = e.target.value;
                                  if (token) {
                                    updateElement(el.id, {
                                      value: el.value ? `${el.value} ${token}` : token,
                                    });
                                  }
                                }}
                                title="Insert a placeholder that auto-fills per driver"
                                className="h-9 rounded-md border border-input bg-background px-2 text-xs text-foreground sm:w-40"
                              >
                                <option value="">+ Insert…</option>
                                {PAYSLIP_PLACEHOLDERS.map((p) => (
                                  <option key={p.token} value={p.token}>
                                    {p.label}
                                  </option>
                                ))}
                              </select>
                            </div>
                            <p className="text-[10px] text-muted-foreground">
                              Use +Insert to add Driver Name or Week Ending — they fill in automatically per driver.
                            </p>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Global styling */}
            <div className="rounded-xl border border-yellow-900/30 bg-black/20 p-3">
              <h3 className="mb-3 font-semibold text-foreground">Style &amp; Options</h3>
              <p className="mb-3 text-[11px] text-muted-foreground">
                The title, tagline, footer note and whether the amounts table shows are now set by
                the blocks above (Heading, Tagline, Footer Note, Amounts Table) -- add, edit or
                remove them there.
              </p>
              <div>
                <Label className="text-xs text-yellow-500">Accent Colour</Label>
                <div className="flex gap-2 max-w-xs">
                  <Input
                    type="color"
                    value={config.branding.accentColor || "#16a34a"}
                    onChange={(e) => updateBranding({ accentColor: e.target.value })}
                    className="h-10 w-14 p-1"
                  />
                  <Input
                    value={config.branding.accentColor}
                    onChange={(e) => updateBranding({ accentColor: e.target.value })}
                    placeholder="#16a34a"
                    className="dark-input"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* ---------------- Right: live mock PDF ---------------- */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-foreground">Mock PDF Preview</h3>
              <div>
                <select
                  value={sampleDriver}
                  onChange={(e) => setSampleDriver(e.target.value)}
                  className="h-8 w-44 rounded-md border border-input bg-background px-2 text-xs text-foreground"
                >
                  <option value="">Company / standard %</option>
                  {driverNames.map((n) => (
                    <option key={n} value={n}>
                      {n}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="xl:sticky xl:top-4">
              <PayslipPreview config={config} />
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default DriverPaymentBuilder;
